import React, { useEffect, useRef } from 'react';
import Header from "@Layout/Header";
import Footer from "@Layout/Footer";
import MediaModal from '@Ui/MediaModal';
import { updateResized, useStore, setSmoothScroll } from "@UseCase/store";
import { getClampValue } from '@UseCase/style';
import NationalRegular from '@Src/fonts/National_2/national-2-regular.woff2';
import NationalBold from '@Src/fonts/National_2/national-2-extrabold.woff2';
import Signifier from '@Src/fonts/Signifier/signifier-light.woff2';
import SignifierItalic from '@Src/fonts/Signifier/Signifier-RegularItalic.woff2';
import Bravebison from '@Src/fonts/Bravebison/bravebison.woff2';
import { Helmet } from 'react-helmet';
import debounce from 'lodash/debounce';
import gsap from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { isSafari, isEdge, isIOS } from 'react-device-detect';
import PropTypes from 'prop-types';

gsap.registerPlugin(ScrollSmoother);

const LayoutWrapper = ({ children, location }) => {
  const [store, dispatch] = useStore();
  const $header = useRef();

  // ✅ viewport height (safe)
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
  };

  // ✅ header height
  const setHeaderHeight = () => {
    if ($header.current) {
      const newHeight = $header.current.getBoundingClientRect().height;
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--headerHeight', `${newHeight}px`);
      });
    }
  };

  // ✅ container left offset
  const setContainerOffset = () => {
    const container = document.querySelector('[data-cont]');
    if (container) {
      const leftOffset = container.getBoundingClientRect().left;
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--left-offset', `${leftOffset + 16}px`);
      });
    }
  };

  // ✅ init GSAP smooth scroll (only once)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      let smoother = ScrollSmoother.get();
      if (!smoother) {
        smoother = ScrollSmoother.create({
          smooth: 1.3,
          wrapper: '#gatsby-focus-wrapper',
          content: '#smooth-content',
          normalizeScroll: isIOS,
          effects: true,
        });
      }
      setSmoothScroll(dispatch, smoother);
    }
  }, [dispatch]);

  // ✅ resize handling
  useEffect(() => {
    setViewportHeight();
    setHeaderHeight();
    setContainerOffset();

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        requestAnimationFrame(() => {
          setViewportHeight();
          setHeaderHeight();
          setContainerOffset();
          updateResized(dispatch);
        });
      }, 100)
    );

    resizeObserver.observe(document.body);
    return () => resizeObserver.disconnect();
  }, [dispatch]);

  // ✅ robust scroll to top on route change & recalculate container offset
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const smoother = gsap.core.globals().ScrollSmoother?.get();

    const scrollToTop = () => {
      const activeSmoother = store?.smoothScroll || smoother;

      if (activeSmoother) {
        requestAnimationFrame(() => {
          activeSmoother.scrollTo(0, false);
        });
      } else {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      }
    };

    scrollToTop();
    setContainerOffset();

    const retry = setTimeout(() => {
      scrollToTop();
      setContainerOffset();
    }, 100);

    const retry2 = setTimeout(() => {
      scrollToTop();
      setContainerOffset();
    }, 300);

    return () => {
      clearTimeout(retry);
      clearTimeout(retry2);
    };
  }, [location.pathname, store?.smoothScroll]);

  // ✅ route-updated event listener fallback
  useEffect(() => {
    const handleRouteUpdate = () => {
      const smoother = gsap.core.globals().ScrollSmoother?.get();
      if (smoother) smoother.scrollTo(0, true);
      setContainerOffset();
    };
    window.addEventListener("gatsby:routeUpdated", handleRouteUpdate);
    return () => window.removeEventListener("gatsby:routeUpdated", handleRouteUpdate);
  }, []);

  // ✅ Initialize LinkedIn Insight Tag at the footer/end lifecycle phase
  useEffect(() => {
    if (typeof window === 'undefined') return;

    window._linkedin_partner_id = "10026258";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);

    if (!window.lintrk) {
      window.lintrk = function(a, b) {
        window.lintrk.q.push([a, b]);
      };
      window.lintrk.q = [];
      
      const s = document.getElementsByTagName("script")[0];
      const b = document.createElement("script");
      b.type = "text/javascript";
      b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    }
  }, []);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="preconnect" href="https://www.wearemtm.com/" crossOrigin />

        {/* ✅ preload fonts early */}
        <link rel="preload" href={NationalRegular} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={NationalBold} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={Signifier} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={SignifierItalic} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={Bravebison} as="font" type="font/woff2" crossOrigin="anonymous" />

        <style>{`
          :root {
            --mega-font-size: ${getClampValue(80, 220)};
            --xxl-font-size: ${getClampValue(50, 135)};
            --5xl-font-size: ${getClampValue(65, 90)};
            --4xl-font-size: ${getClampValue(43, 62)};
            --3xl-font-size: ${getClampValue(34, 48)};
            --2xl-font-size: ${getClampValue(28, 42)};
            --xl-font-size: ${getClampValue(20, 30)};
            --lg-font-size: ${getClampValue(16, 20)};
            --box-size-half: ${getClampValue(35, 75)};
            --box-size: ${getClampValue(70, 150)};
            --boxh-size: ${getClampValue(110, 225)};
            --box2-size: ${getClampValue(140, 300)};
            --box3-size: ${getClampValue(210, 450)};
            --color-orange: #FF4F17;
            --left-offset: 16px;
          }
          @font-face {
            font-family: 'National';
            src: url(${NationalRegular}) format('woff2');
            font-weight: 200;
            font-display: swap;
            font-style: normal;
          }
          @font-face {
            font-family: 'National';
            src: url(${NationalBold}) format('woff2');
            font-weight: 700;
            font-display: swap;
            font-style: normal;
          }
          @font-face {
            font-family: 'Signifier';
            src: url(${Signifier}) format('woff2');
            font-weight: 300;
            font-display: swap;
            font-style: normal;
          }
          @font-face {
            font-family: 'Signifier';
            src: url(${SignifierItalic}) format('woff2');
            font-weight: 300;
            font-display: swap;
            font-style: italic;
          }
          @font-face {
            font-family: 'Bravebison';
            src: url(${Bravebison}) format('woff2');
            font-weight: 900;
            font-display: swap;
            font-style: normal;
          }

          .item-title,
          #smooth-content,
          section,
          footer {
            will-change: transform, opacity;
          }

          footer { overflow: hidden; }
          section { margin-bottom: -1px; overflow: hidden; }
        `}</style>
      </Helmet>

      <Header ref={$header} location={location} />

      <div
        id="smooth-content"
        className={`${(isSafari || isEdge) ? 'boxedDevices' : ''} content changed`}
      >
        <main>{children}</main>
        <Footer location={location} />
      </div>

      <MediaModal />

      {/* ✅ LinkedIn NoScript Fallback placed at the bottom of the layout structure */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=10026258&fmt=gif"
        />
      </noscript>
    </>
  );
};

LayoutWrapper.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object,
};

export default LayoutWrapper;